// src/components/common/ActiveUsersDisplay.js

import React, { useState, useEffect, useContext } from 'react';
import { Typography, Box, CircularProgress } from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { styled } from '@mui/system';
import AppContext from '../../../../../../context/AppProvider';

const sendSignalRSafe = async (connection, sendFunction) => {
  if (connection && connection.state === 'Connected') {
    try {
      await sendFunction(connection);
    } catch (error) {
      console.error('Error sending SignalR message:', error);
      // Handle error appropriately, e.g., retry logic or user notifications
    }
  } else {
    console.warn('SignalR connection is not established.');
    // Optionally, you can try to reconnect or notify the user
  }
};

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: theme.spacing(1),
}));

const Label = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  fontWeight: 500,
}));

const Count = styled(Typography)(({ color, theme }) => ({
  marginLeft: theme.spacing(1),
  color: color || 'var(--orange)',
  fontWeight: 700,
  fontSize: '1rem',
}));

const ActiveUsersDisplay = () => {
  const { signalRUserConnection } = useContext(AppContext);
  const [activeUsersStat, setActiveUsersStat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!signalRUserConnection) return;

    const initialize = async () => {
      try {
        if (!activeUsersStat) {
          await sendSignalRSafe(signalRUserConnection, async (connection) => {
            await connection.send('GetUserCountStaistic');
          });
        }
      } catch (err) {
        setError('Failed to fetch active users statistics.');
      } finally {
        setLoading(false);
      }
    };

    initialize();

    const handleUpdateUserCount = (userCount) => {
      setActiveUsersStat(userCount);
      setLoading(false);
    };

    signalRUserConnection.on('UpdateUserCount', handleUpdateUserCount);

    return () => {
      signalRUserConnection.off('UpdateUserCount', handleUpdateUserCount);
    };
  }, [signalRUserConnection, activeUsersStat]);

  if (loading) {
    return (
      <Container>
        <CircularProgress size={20} />
        <Label variant="body1" sx={{ marginLeft: '0.5em', whiteSpace:"nowrap"  }}>
          Active Users
        </Label>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container>
      <GroupOutlinedIcon color="primary" />
      <Label variant="body1" sx={{ whiteSpace:"nowrap" }}>Active Users</Label>
      <Count color="var(--orange)" variant="body1">
        {activeUsersStat?.swarmBuyRequest?.activeUsersWithSwarms ?? 0}
      </Count>
    </Container>
  );
};

export default ActiveUsersDisplay;
